/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Descriptions, Typography, Empty, Button, Tooltip } from 'antd';
import React, { FC, Fragment, useState } from 'react';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './LeftDetailsBlock.style';
import { useIntl } from 'react-intl';
import {
  RegistrationLabelValuesVM,
  TenderRegistrationPublicVM,
} from '~src/types/db';
import IconButton from '~components/_common/IconButton/IconButton';
import { MainModalWindow } from '~components/popups-modals/DetailsLayouts/MainModalWindow';
import { DatesModalWindow } from '~components/popups-modals/DetailsLayouts/DatesModalWindow';
import { LabelsModalWindow } from '~components/popups-modals/DetailsLayouts/LabelsModalWindow';
import moment from 'moment';
import {
  getPublicShareStatus,
  publicShareStatusIntlId,
} from '~components/pages/PublicShare/PublicShare.util';

const { Title } = Typography;

type LeftDetailsBlockProps = {
  isTenderPage?: boolean;
  isPublicTender?: boolean;
  item: {
    id?: string;
    title?: string;
    reference?: string;
    share_title?: string;
    descriptionTitle?: string;
    description?: string;
    projectId?: string;
    tenderId?: string;
    tableEntries: {
      active_from?: number;
      deadline_at?: number;
      publication_date?: number;
      active_until?: number;
      qa_start?: number;
      qa_stop?: number;
      qa_on?: boolean;
      results_deadline?: number;
      results_details?: string;
      execution_period?: number;
      execution_period_data?: string;
      publicShareState?: number;
    };
    label_values: RegistrationLabelValuesVM[];
    global_serial?: string;
    public?: TenderRegistrationPublicVM | null;
  };
};

const LeftDetailsBlock: FC<LeftDetailsBlockProps> = ({
  item,
  isTenderPage,
  isPublicTender,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();

  const [isMainEditModalOpen, setMainModalEditOpen] = useState(false);
  const [isDetailsEditModalOpen, setDetailsModalEditOpen] = useState(false);
  const [isLabelsModalOpen, setLabelsModalOpen] = useState(false);

  const hasLabelValues = item.label_values?.length > 0;
  const shouldShowLabelsSection = isTenderPage || hasLabelValues;

  const EmptyBlock: React.FC<{ onClick: () => void }> = (props) => {
    return (
      <Empty
        css={{
          padding: '5vh 0',
          margin: '0',
          border: '1px solid #E3E4E4',
          borderRadius: '5px',
        }}
        description={props.children}>
        {isTenderPage && (
          <Button
            type="primary"
            css={[styles.buttons, styles.blue]}
            onClick={props.onClick}>
            {intl.formatMessage({ id: 'add' })}
          </Button>
        )}
      </Empty>
    );
  };

  const transformedDescription = (item.description ?? '').replace(
    /(\r\n|\n|\r)/g,
    '<br />'
  );

  const publicStatus =
    isTenderPage && item.public ? getPublicShareStatus(item.public) : null;

  const getTenderStateLabel = (state: number): string => {
    switch (state) {
      case 1:
        return intl.formatMessage({
          id: 'tender_state_draft',
        });
      case 3:
        return intl.formatMessage({
          id: 'tender_state_active',
        });
      case 6:
        return intl.formatMessage({
          id: 'tender_state_closed',
        });
      case 7:
        return intl.formatMessage({
          id: 'tender_state_processed',
        });
      default:
        return intl.formatMessage({
          id: 'tender_state_unknown',
        });
    }
  };

  const publicStatusLabel =
    isPublicTender && item.tableEntries.publicShareState
      ? getTenderStateLabel(item.tableEntries.publicShareState)
      : publicStatus
      ? intl.formatMessage({
          id: publicShareStatusIntlId[publicStatus],
        })
      : null;

  // console.log(`items leftBlock`, item);
  // console.log(`items leftBlock`, item);

  return (
    <div>
      {isTenderPage && isMainEditModalOpen && (
        <MainModalWindow
          visible={isMainEditModalOpen}
          onOk={() => setMainModalEditOpen(false)}
          onCancel={() => setMainModalEditOpen(false)}
          item={item}
        />
      )}
      {isTenderPage && isDetailsEditModalOpen && (
        <DatesModalWindow
          visible={isDetailsEditModalOpen}
          onOk={() => setDetailsModalEditOpen(false)}
          onCancel={() => setDetailsModalEditOpen(false)}
          item={item}
        />
      )}
      {isTenderPage && isLabelsModalOpen && (
        <LabelsModalWindow
          visible={isLabelsModalOpen}
          onOk={() => setLabelsModalOpen(false)}
          onCancel={() => setLabelsModalOpen(false)}
          item={item}
        />
      )}
      {item?.global_serial || publicStatusLabel ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 4,
          }}>
          {item?.global_serial ? (
            <Tooltip
              title={`${intl.formatMessage({ id: 'gs-long' })}: ${
                item.global_serial
              }`}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {intl.formatMessage({ id: 'gs-short' })}: {item.global_serial}
              </Typography.Text>
            </Tooltip>
          ) : null}

          {publicStatusLabel ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {intl.formatMessage({ id: 'tender-public-status-label' })}:{' '}
              {publicStatusLabel}
            </Typography.Text>
          ) : null}
        </div>
      ) : null}
      {item.reference ? (
        <Typography style={{ color: 'gray', fontSize: '16px' }}>
          {item.reference}
        </Typography>
      ) : null}
      {item.share_title ? (
        <Title style={{ marginTop: '10px' }} level={4}>
          {item.share_title}
        </Title>
      ) : null}
      {item.title ? (
        <Title style={{ marginTop: '10px' }} level={4}>
          {item.title}
        </Title>
      ) : null}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}>
        <p style={{ margin: '0', display: 'flex', alignItems: 'center' }}>
          {item.descriptionTitle ??
            intl.formatMessage({ id: 'description-title' })}
        </p>
        {isTenderPage && (
          <div
            css={styles.buttonDiv}
            onClick={() => setMainModalEditOpen(true)}>
            <IconButton
              icon={['far', 'pen-line']}
              containerStyle={{
                marginLeft: 0,
                color: '#4170ff',
              }}
              backgroundOff
              widthTooltip="300px"
            />
          </div>
        )}
      </div>
      {item.description ? (
        <div
          style={{
            marginTop: '5px',
            color: 'gray',
            maxWidth: '80%',
            whiteSpace: 'pre-wrap',
          }}
          dangerouslySetInnerHTML={{ __html: transformedDescription }}
        />
      ) : (
        <EmptyBlock onClick={() => setMainModalEditOpen(true)}>
          {intl.formatMessage({ id: 'tender-no-description' })}
        </EmptyBlock>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '20px',
        }}>
        <p style={{ margin: '0', display: 'flex', alignItems: 'center' }}>
          {intl.formatMessage({ id: 'tender-details-edit-dates' })}
        </p>
        {isTenderPage && (
          <div
            css={styles.buttonDiv}
            onClick={() => setDetailsModalEditOpen(true)}>
            <IconButton
              icon={['far', 'pen-line']}
              containerStyle={{
                marginLeft: 0,
                color: '#4170ff',
              }}
              backgroundOff
              widthTooltip="300px"
            />
          </div>
        )}
      </div>
      {Object.values(item.tableEntries).every((value) => !value) ? (
        <EmptyBlock onClick={() => setDetailsModalEditOpen(true)}>
          {intl.formatMessage({ id: 'tender-no-dates' })}
        </EmptyBlock>
      ) : (
        <Descriptions bordered column={2} style={{ margin: '20px 0' }}>
          {Object.entries(item.tableEntries).map(([label, value]) => {
            if (!value) return null;

            // Exclude Q&A fields from the generic loop to handle them with custom logic below
            if (
              [
                'results_details',
                'execution_period',
                'results_deadline',
                'execution_period_data',
                'qa_on',
                'qa_start',
                'qa_stop',
                'publicShareState',
              ].includes(label)
            )
              return null;

            let displayValue = value;

            if (typeof value === 'number') {
              displayValue = moment.unix(value).format('DD/MM/YYYY - HH:mm');
            }

            if (typeof value === 'boolean') {
              displayValue = displayValue
                ? intl.formatMessage({ id: 'yes' })
                : intl.formatMessage({ id: 'no' });
            }

            return (
              <Descriptions.Item
                key={label}
                label={intl.formatMessage({ id: `tender-details-${label}` })}>
                {displayValue}
              </Descriptions.Item>
            );
          })}

          {/* Custom Logic for Q&A Fields */}
          {(() => {
            const { qa_on, qa_start, qa_stop, deadline_at } = item.tableEntries;

            // If Q&A is false/undefined, show nothing
            if (!qa_on) return null;

            const qaItems = [];

            // 1. Q&A Start
            if (qa_start) {
              qaItems.push(
                <Descriptions.Item
                  key="qa_start"
                  label={intl.formatMessage({ id: 'tender-details-qa_start' })}>
                  {moment.unix(qa_start).format('DD/MM/YYYY - HH:mm')}
                </Descriptions.Item>
              );
            }

            // 2. Q&A Stop (Fallback: qa_stop -> deadline_at)
            const effectiveStopDate = qa_stop || deadline_at;

            if (effectiveStopDate) {
              qaItems.push(
                <Descriptions.Item
                  key="qa_stop"
                  label={intl.formatMessage({ id: 'tender-details-qa_stop' })}>
                  {moment.unix(effectiveStopDate).format('DD/MM/YYYY - HH:mm')}
                </Descriptions.Item>
              );
            } else {
              // 3. Fallback to Boolean only if both dates are empty
              // If we are here, qa_on is true, so we render "Yes"
              qaItems.push(
                <Descriptions.Item
                  key="qa_on"
                  label={intl.formatMessage({ id: 'tender-details-qa_on' })}>
                  {intl.formatMessage({ id: 'yes' })}
                </Descriptions.Item>
              );
            }

            return qaItems;
          })()}

          {(item.tableEntries.results_deadline ||
            item.tableEntries.results_details) && (
            <Descriptions.Item
              key="announcement_of_results_combined"
              label={intl.formatMessage({
                id: 'tender-details-results_deadline',
              })}>
              <div>
                {item.tableEntries.results_deadline && (
                  <div>
                    {moment
                      .unix(item.tableEntries.results_deadline)
                      .format('DD/MM/YYYY - HH:mm')}
                  </div>
                )}
                {item.tableEntries.results_details && (
                  <div>{item.tableEntries.results_details}</div>
                )}
              </div>
            </Descriptions.Item>
          )}

          {(item.tableEntries.execution_period_data ||
            item.tableEntries.execution_period) && (
            <Descriptions.Item
              key="execution_period_combined"
              label={intl.formatMessage({
                id: 'tender-details-execution_period',
              })}>
              <div>
                {item.tableEntries.execution_period_data &&
                  (() => {
                    const match = /^(\d{4})\.(\d)$/.exec(
                      String(item.tableEntries.execution_period_data)
                    );
                    if (match) {
                      const [, year, quarter] = match;
                      return <div>{`Q${quarter} - ${year}`}</div>;
                    }
                    return <div>{item.tableEntries.execution_period_data}</div>;
                  })()}
                {item.tableEntries.execution_period && (
                  <div>{item.tableEntries.execution_period}</div>
                )}
              </div>
            </Descriptions.Item>
          )}
        </Descriptions>
      )}

      {shouldShowLabelsSection && (
        <Fragment>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '20px',
            }}>
            <p style={{ margin: '0', display: 'flex', alignItems: 'center' }}>
              {intl.formatMessage({ id: 'tender-details-custom-fields' })}
            </p>
            {isTenderPage && (
              <div
                css={styles.buttonDiv}
                onClick={() => setLabelsModalOpen(true)}>
                <IconButton
                  icon={['far', 'pen-line']}
                  containerStyle={{
                    marginLeft: 0,
                    color: '#4170ff',
                  }}
                  backgroundOff
                  widthTooltip="300px"
                />
              </div>
            )}
          </div>
          {!item.label_values.length ? (
            <EmptyBlock onClick={() => setLabelsModalOpen(true)}>
              {intl.formatMessage({ id: 'tender-custom-fields' })}
            </EmptyBlock>
          ) : (
            <div>
              {item.label_values.map((group) => (
                <div>
                  <p>
                    <strong>{group.group_name}</strong>
                  </p>
                  <Descriptions
                    bordered
                    style={{ margin: '10px 0' }}
                    column={1}>
                    {group.labels.map((label) => (
                      <Descriptions.Item
                        contentStyle={{ width: '68.8%' }}
                        label={label.label_title}
                        key={label.label_id}>
                        {label.value}
                      </Descriptions.Item>
                    ))}
                  </Descriptions>
                </div>
              ))}
            </div>
          )}
        </Fragment>
      )}
    </div>
  );
};

export default LeftDetailsBlock;
